import React from 'react';

import backend from 'utils/backend';

const useCommand = (props) => {
  const {
    requester,
    skills,
    statusProgressWrap: progressWrap,
    task = {},
  } = props;

  const {
    clientRn = null,
    connections: connectionRns = [],
    engagementRuleRn = null,
    playbookEngagementPlan = null,
    skillRn = null,
    taskRn,
    status: taskStatus,
  } = task;

  const [response, setResponse] = React.useState(null);
  const [lastExecutedCommand, setLastExecutedCommand] = React.useState(null);
  const [commandExecutionContext, setCommandExecutionContext] = React.useState(null);
  const [isExecutingCommand, setIsExecutingCommand] = React.useState(false);
  const communicationTarget = commandExecutionContext?.communicationTarget || null;
  const engagementResponseType = playbookEngagementPlan?.responseType || null;
  const isTaskComplete = taskStatus === 'COMPLETE';

  const skill = React.useMemo(() => {
    if (!skillRn) {
      return null;
    }

    return skills.find((entry) => entry.skillRn === skillRn) || null;
  }, [skillRn, skills]);

  React.useEffect(() => {
    setResponse(null);
    setLastExecutedCommand(null);
    setCommandExecutionContext(null);
  }, [engagementRuleRn, skillRn, taskRn]);

  const getSkillResources = React.useCallback((runtimeConnections) => {
    return [
      clientRn,
      ...connectionRns,
      ...runtimeConnections.map((connection) => connection.connectionRn),
    ]
        .filter(Boolean)
        .reduce((resources, resourceRn) => {
          const resourceType = /^RN:CLIENT-V1-[^:]+$/.test(resourceRn) ?
            'CLIENT' :
            /^RN:CAREGIVER-V1-[^:]+$/.test(resourceRn) ?
              'CAREGIVER' :
              null;

          if (!resourceType || resources.some((resource) => resource.rn === resourceRn)) {
            return resources;
          }

          return [
            ...resources,
            {
              type: resourceType,
              rn: resourceRn,
            },
          ];
        }, []);
  }, [clientRn, connectionRns]);

  const hydrateTaskForRun = React.useCallback(async () => {
    const connectionInfo = await Promise.all(connectionRns.map(async (connectionRn) => {
      try {
        if (/^RN:CLIENT-V1-[^:]+$/.test(connectionRn)) {
          const clientResponse = await backend.client.getClient(connectionRn, false);
          const clientNameParts = (clientResponse?.clientDetail?.clientName || '').trim().split(/\s+/).filter(Boolean);
          const [firstName = '', ...lastNameParts] = clientNameParts;
          const relationships = Object.values(clientResponse?.clientDetail?.relationships || {})
              .filter((relationship) => {
                return relationship?.relationshipRn &&
                  [
                    relationship?.name,
                    relationship?.contacts?.find((contact) => contact?.type === 'Email')?.detail,
                    relationship?.contacts?.find((contact) => contact?.type === 'Phone')?.detail,
                  ].some(Boolean);
              })
              .sort((firstRelationship, secondRelationship) => {
                return firstRelationship?.priority - secondRelationship?.priority;
              });

          const defaultRelationship = relationships.find((relationship) => {
            return Number(relationship?.priority) === 1;
          }) || relationships[0] || null;

          return {
            connectionRn,
            firstName,
            lastName: lastNameParts.join(' '),

            communicationTarget: {
              defaultRelationship,
              relationships,
              getRelationshipEmail: (relationship) => relationship?.contacts?.find((contact) => {
                return contact?.type === 'Email';
              })?.detail || '',
              getRelationshipSms: (relationship) => relationship?.contacts?.find((contact) => {
                return contact?.type === 'Phone';
              })?.detail || '',

              sendEmail: async (emailData, targetRelationshipRn, onSuccess=()=>undefined, onError=()=>undefined) => {
                const selectedRelationship = relationships.find((relationship) => {
                  return relationship.relationshipRn === targetRelationshipRn;
                }) || defaultRelationship;
                const relationshipRn = selectedRelationship?.relationshipRn;

                if (!relationshipRn) {
                  onError();
                  return null;
                }

                return progressWrap(async () => {
                  await backend.notification.sendEmail(relationshipRn, emailData);
                  onSuccess();
                }, {debugString: 'CLIENT_MESSAGE_SEND_EMAIL'});
              },

              sendSms: async (smsData, requestUser, targetRelationshipRn, onSuccess=()=>undefined, onError=()=>undefined) => {
                const selectedRelationship = relationships.find((relationship) => {
                  return relationship.relationshipRn === targetRelationshipRn;
                }) || defaultRelationship;
                const relationshipRn = selectedRelationship?.relationshipRn;

                if (!relationshipRn) {
                  onError();
                  return null;
                }

                return progressWrap(async () => {
                  await backend.notification.sendSms(
                      relationshipRn,
                      smsData.message,
                      smsData.fromEmail || requestUser?.email,
                  );
                  onSuccess();
                }, {debugString: 'CLIENT_MESSAGE_SEND_SMS'});
              },

              updateCareplan: async (careplan, progressOptions={}) => {
                return progressWrap(async () => {
                  await backend.client.updateCareplan(connectionRn, careplan);
                }, {
                  ...progressOptions,
                  debugString: 'CLIENT_CAREPLAN_UPDATE',
                });
              },
            },
          };
        }

        if (/^RN:CAREGIVER-V1-[^:]+$/.test(connectionRn)) {
          const caregiverResponse = await backend.caregiver.getCaregiver(connectionRn, false);
          const caregiverNameParts = (caregiverResponse?.caregiverDetail?.caregiverName || '').trim().split(/\s+/).filter(Boolean);
          const [firstName = '', ...lastNameParts] = caregiverNameParts;
          const relationship = {
            email: caregiverResponse?.caregiverBio?.questions?.email?.questionData?.value || '',
            name: caregiverResponse?.caregiverDetail?.caregiverName || '',
            phoneNumber: caregiverResponse?.caregiverBio?.questions?.phoneNumber?.questionData?.value || '',
            relationshipRn: connectionRn,
          };
          const relationships = [relationship].filter((entry) => {
            return [entry.name, entry.email, entry.phoneNumber].some(Boolean);
          });

          return {
            connectionRn,
            firstName,
            lastName: lastNameParts.join(' '),

            communicationTarget: {
              defaultRelationship: relationships[0] || null,
              relationships,
              getRelationshipEmail: (relationshipOption) => relationshipOption?.email,
              getRelationshipSms: (relationshipOption) => relationshipOption?.phoneNumber,

              sendEmail: async (emailData, _relationshipRn, onSuccess=()=>undefined, _onError=()=>undefined) => {
                return progressWrap(async () => {
                  await backend.notification.sendEmail(connectionRn, emailData);
                  onSuccess();
                }, {debugString: 'CAREGIVER_MESSAGE_SEND_EMAIL'});
              },
            },
          };
        }
      } catch {
        return null;
      }

      return null;
    }));

    return {
      connectionInfo: connectionInfo.filter(Boolean),
    };
  }, [connectionRns, progressWrap]);

  const executeSkill = React.useCallback(async (selectedSkill, promptText=' ') => {
    let executionResponse = null;

    await progressWrap(async () => {
      try {
        setIsExecutingCommand(true);
        const taskRunData = await hydrateTaskForRun();
        const taskRunConnections = taskRunData.connectionInfo || [];
        const requestBody = {
          ressources: [
            ...getSkillResources(taskRunConnections),
            {type: 'USER', rn: requester.userRn},
          ],
          text: promptText || ' ',
        };

        setLastExecutedCommand({
          type: 'skill',
          promptText,
          skill: selectedSkill,
        });

        setCommandExecutionContext({
          communicationTarget: taskRunConnections[0]?.communicationTarget || null,
        });

        executionResponse = await backend.skills.executeSkill(selectedSkill.skillRn, requestBody);
        setResponse(executionResponse?.result || null);
      } finally {
        setIsExecutingCommand(false);
      }
    }, {
      showProgress: false,
    });

    return executionResponse;
  }, [getSkillResources, hydrateTaskForRun, progressWrap, requester?.userRn]);

  const handleExecuteSkill = React.useCallback(async () => {
    if (skill?.skillRn && !isExecutingCommand) {
      await executeSkill(skill);
    }
  }, [executeSkill, isExecutingCommand, skill]);

  const handleExecuteEngagement = React.useCallback(async () => {
    let executionResponse = null;

    await progressWrap(async () => {
      try {
        setIsExecutingCommand(true);
        const taskRunData = await hydrateTaskForRun();
        const taskRunConnections = taskRunData.connectionInfo || [];

        setLastExecutedCommand({
          type: 'engagement',
        });

        setCommandExecutionContext({
          communicationTarget: taskRunConnections[0]?.communicationTarget || null,
        });

        executionResponse = await backend.playbook.generateEngagementResponse(playbookEngagementPlan);
        executionResponse = executionResponse?.result || executionResponse || {};
        setResponse({
          ...executionResponse,
          outputType: executionResponse.outputType || executionResponse.responseType || engagementResponseType,
          subject: executionResponse.subject || '',
          content: executionResponse.content || executionResponse.message || '',
          message: executionResponse.message || executionResponse.content || '',
        });
      } finally {
        setIsExecutingCommand(false);
      }
    }, {
      showProgress: false,
    });

    return executionResponse;
  }, [engagementResponseType, hydrateTaskForRun, playbookEngagementPlan, progressWrap]);

  const regenerate = React.useCallback(() => {
    if (!lastExecutedCommand) {
      return null;
    }

    if (lastExecutedCommand.type === 'skill') {
      return executeSkill(lastExecutedCommand.skill, lastExecutedCommand.promptText);
    }

    if (lastExecutedCommand.type === 'engagement') {
      return handleExecuteEngagement();
    }

    return null;
  }, [executeSkill, handleExecuteEngagement, lastExecutedCommand]);

  const discard = React.useCallback(() => {
    setResponse(null);
    setCommandExecutionContext(null);
  }, []);

  const canRunSkillTask = Boolean(!isTaskComplete && skill?.skillRn);
  const canRunEngagementTask = Boolean(!isTaskComplete && engagementRuleRn && engagementResponseType && playbookEngagementPlan);
  const canRunCommand = canRunSkillTask || canRunEngagementTask;
  const runCommand = canRunSkillTask ? handleExecuteSkill : handleExecuteEngagement;

  return {
    canRunCommand,
    communicationTarget,
    discard,
    isExecutingCommand,
    regenerate,
    response,
    runCommand,
  };
};

export default useCommand;
